import { ShieldCheck, Layers, GitBranch, Fingerprint } from "lucide-react";

/**
 * R&D Bottleneck marketing section — Phase 30, Phase 5.
 * Landing-page-facing pitch that reframes the platform as the antidote
 * to the "signal noise" problem in modern R&D. Broken into digestible
 * feature slabs rather than the wall-of-text form the user shipped.
 */
export default function RDBottleneckSection() {
  return (
    <section
      id="trust-core"
      data-testid="landing-rd-bottleneck"
      className="relative z-10 max-w-7xl mx-auto px-6 pb-16"
    >
      <div className="border border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.06] via-transparent to-cyan-500/[0.04] p-6 sm:p-10">
        <div className="flex items-center gap-2 text-emerald-300 mb-2">
          <ShieldCheck size={16} />
          <span className="text-[10px] font-mono uppercase tracking-[0.25em]">
            THE_R&D_BOTTLENECK · SOLVED
          </span>
        </div>
        <h2 className="font-mono text-3xl sm:text-4xl tracking-tight leading-tight max-w-4xl">
          From raw signal to{" "}
          <span className="text-emerald-300">trusted knowledge</span> —
          without the noise.
        </h2>
        <p className="mt-3 text-[13.5px] text-zinc-300 leading-relaxed max-w-3xl">
          Modern research doesn&apos;t suffer from a lack of information.
          It suffers from an inability to separate <b>evidence</b> from{" "}
          <b>speculation</b>. MindStein is a governed, high-assurance
          environment where every claim is anchored to sources, ranked
          by a deterministic core, and only promoted into your permanent
          library once it survives nine layers of validation.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Pillar
            icon={Fingerprint}
            tone="text-emerald-300 border-emerald-500/40"
            tag="VERIFIED PROVENANCE"
            title="Every signal is anchored."
            body="Two independent sources per claim, cross-checked against a 200-resource whitelist of top universities and science outlets. No orphan facts."
          />
          <Pillar
            icon={GitBranch}
            tone="text-cyan-300 border-cyan-500/40"
            tag="STRUCTURED REASONING"
            title="Hypotheses stay traceable."
            body="The engine identifies patterns and generates hypotheses — but every downstream inference remains linked to the originating evidence."
          />
          <Pillar
            icon={Layers}
            tone="text-amber-300 border-amber-500/40"
            tag="TRUST MATURATION"
            title="Nine layers before promotion."
            body="A deterministic ranker (score ≥ 0.85) promotes items into the permanent library. Weak signals stay in formative/verification stages until they earn their place."
          />
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Badge>DB4_PROMOTION_GATE · 0.85</Badge>
          <Badge>2_INDEPENDENT_REFS_MINIMUM</Badge>
          <Badge>200_WHITELISTED_SOURCES</Badge>
          <Badge>DAILY_ROTATION · 2_PER_CATEGORY</Badge>
        </div>
      </div>
    </section>
  );
}

function Pillar({ icon: Icon, tone, tag, title, body }) {
  const parts = tone.split(" ");
  const text = parts[0];
  const border = parts.slice(1).join(" ");
  return (
    <div className={`border ${border} bg-[#0A0A0A] p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] uppercase tracking-[0.2em] font-mono ${text}`}>
          {tag}
        </span>
        <Icon size={14} className={text} />
      </div>
      <div className="font-mono text-[15px] text-white leading-tight">{title}</div>
      <p className="mt-2 text-[12px] text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1 border border-[#27272A] bg-[#0A0A0A] text-[10px] font-mono uppercase tracking-wider text-zinc-400 px-2 py-1">
      {children}
    </span>
  );
}
